import { ApolloClient, ApolloLink, InMemoryCache, split } from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import ApolloLinkTimeout from 'apollo-link-timeout';
import { createClient } from 'graphql-ws';

import { toast } from '@/common/components/shared/toaster';
import { fragments } from '@/common/graphql';

import { getAuthToken, removeAuthUserInSession } from './auth';
import { ApiError } from './constants';

const shortTimeoutLink = new ApolloLinkTimeout(60 * 1000);

const wsLink = new GraphQLWsLink(
  createClient({
    url: `${import.meta.env.VITE_GRAPHQL_WS_ENDPOINT}`,
    connectionParams: () => {
      const token = getAuthToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
  }),
);

const httpLink = new BatchHttpLink({
  uri: `${import.meta.env.VITE_GRAPHQL_ENDPOINT}`,
  batchMax: 10,
  credentials: 'include', // To enable Session Affinity on Backend (use Cookie)
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = getAuthToken();
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const authenticationAfterware = new ApolloLink((operation, forward) =>
  forward(operation).map(response => {
    const apiError = response.errors?.[0].message;
    if (apiError === ApiError.AUTHENTICATION_FAILED) {
      removeAuthUserInSession();
      window.location.replace('/login');
    }
    return response;
  }),
);

const errorLink = onError(error => {
  if (error.networkError) {
    toast.clean();
    toast.error('Network error occurred. Please wait at least 30 seconds and try again later.');
  }
});

const baseApolloLink = ApolloLink.from([errorLink, authLink, authenticationAfterware]);

const cache = new InMemoryCache({
  possibleTypes: fragments.possibleTypes,
  typePolicies: {
    Employee: {
      fields: {
        generalInformation: {
          // EmployeeGeneralInformation has no `id`, so different queries fetching different,
          // non-overlapping field subsets would otherwise silently drop each other's cached
          // fields on write. Shallow-merge instead of replacing.
          merge: (existing, incoming) => ({ ...existing, ...incoming }),
        },
      },
    },
    EmployeeIdp: {
      fields: {
        keyAreas: {
          // keyAreas is always fetched in full (never paginated), so the incoming array is
          // always the complete, authoritative set - always take it instead of Apollo's default
          // merge-by-index, which would leave stale entries behind after a removal.
          merge: (_existing, incoming) => incoming,
        },
      },
    },
  },
});

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  baseApolloLink.concat(shortTimeoutLink.concat(httpLink)),
);

export const apolloClient = new ApolloClient({
  link: splitLink,
  cache,
});

if (process.env.NODE_ENV !== 'production') {
  // Adds messages only in a dev environment
  loadDevMessages();
  loadErrorMessages();
}
